import React, { useCallback, useRef, useState } from 'react';

import apiClient from '../../services/api';
import { ProjectAsset } from '../../types/editor';

function classify(file: File): 'image' | 'video' | 'audio' | null {
  const top = (file.type || '').split('/')[0];
  if (top === 'image' || top === 'video' || top === 'audio') return top;
  return null;
}

interface AssetUploaderProps {
  projectId: number | null;
  onUploaded?: (asset: ProjectAsset) => void;
}

export default function AssetUploader({ projectId, onUploaded }: AssetUploaderProps) {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const upload = useCallback(async (fileList: FileList | null): Promise<void> => {
    if (!projectId) return;
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setError(null);
    setUploading((n) => n + files.length);
    try {
      for (const file of files) {
        const kind = classify(file);
        if (!kind) continue;
        const form = new FormData();
        form.append('file', file);
        form.append('kind', kind);
        const asset = await apiClient.uploadFile<ProjectAsset>(
          `/editor/projects/${projectId}/upload_asset/`, form,
        );
        onUploaded?.(asset);
      }
    } catch (err) {
      setError((err as Error).message || 'Upload failed');
    } finally {
      setUploading(0);
    }
  }, [projectId, onUploaded]);

  if (!projectId) return null;

  return (
    <div className="asset-uploader">
      <div
        className={`asset-drop-zone ${dragOver ? 'is-over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*,video/*,audio/*"
          multiple
          hidden
          onChange={(e) => { upload(e.target.files); e.target.value = ''; }}
        />
        <div className="asset-drop-title">
          {uploading > 0
            ? `Uploading ${uploading} file${uploading === 1 ? '' : 's'}…`
            : (dragOver ? 'Drop to upload' : 'Drag in images, video, or audio')}
        </div>
        <div className="asset-drop-sub muted">or click to browse</div>
      </div>
      {error && <div className="posts-error">{error}</div>}
    </div>
  );
}
